import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Wheat,
    ChefHat,
    ShoppingBasket,
    User,
    LogOut,
    BarChart3,
    Truck,
    PackageCheck,
    BookOpen,
    Wine,
    Sparkles,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { LOGO_URL } from "../lib/constants";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/ingredients", label: "Ingredienti", icon: Wheat, testid: "nav-ingredients" },
    { to: "/recipes", label: "Piatti", icon: ChefHat, testid: "nav-recipes" },
    { to: "/purchases", label: "Acquisti", icon: ShoppingBasket, testid: "nav-purchases" },
    { to: "/orders", label: "Ordini", icon: PackageCheck, testid: "nav-orders", proBadge: true },
    { to: "/beverage-cost", label: "Beverage Cost", icon: Wine, testid: "nav-beverage-cost" },
    { to: "/suppliers", label: "Fornitori", icon: Truck, testid: "nav-suppliers" },
    { to: "/stats", label: "Statistiche", icon: BarChart3, testid: "nav-stats" },
    { to: "/guide", label: "Guida", icon: BookOpen, testid: "nav-guide" },
    { to: "/profile", label: "Profilo", icon: User, testid: "nav-profile" },
];

export function Sidebar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const initial = (user?.name || "R").charAt(0).toUpperCase();

    return (
        <aside
            data-testid="sidebar"
            className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] bg-brand-navy text-white flex-col z-30"
        >
            <div className="px-5 pt-6 pb-5 border-b border-white/10">
                <div className="rounded-xl bg-white/95 px-3 py-3 flex items-center justify-center">
                    <img
                        src={LOGO_URL}
                        alt="FoodCost by Renova360"
                        className="w-full max-w-[200px] h-auto object-contain"
                        data-testid="sidebar-logo"
                    />
                </div>
            </div>

            <nav className="flex-1 px-3 py-6 space-y-1">
                {NAV.map(({ to, label, icon: Icon, testid, proBadge }) => (
                    <NavLink
                        key={to}
                        to={to}
                        data-testid={testid}
                        className={({ isActive }) =>
                            `group flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                                isActive
                                    ? "bg-brand-gold/15 text-brand-gold"
                                    : "text-white/75 hover:text-white hover:bg-white/5"
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <Icon size={18} strokeWidth={isActive ? 2.4 : 1.8} />
                                <span className="text-[14px] font-medium tracking-wide">{label}</span>
                                {proBadge && (
                                    <span className="ml-auto text-[9px] uppercase tracking-[0.16em] font-bold rounded-sm px-1.5 py-0.5 bg-brand-gold/20 text-brand-gold">
                                        Pro
                                    </span>
                                )}
                                {isActive && !proBadge && (
                                    <span className="ml-auto h-1.5 w-1.5 rounded-full bg-brand-gold" />
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="px-4 pb-3">
                <AiCreditsWidget />
            </div>

            <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                    <div
                        className="h-10 w-10 rounded-full flex items-center justify-center font-display text-lg"
                        style={{ background: "#c9a961", color: "#1a2a44" }}
                    >
                        {initial}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">
                            {user?.name}
                        </div>
                        <div className="text-[11px] text-white/50 truncate">{user?.email}</div>
                    </div>
                    <button
                        data-testid="sidebar-logout"
                        onClick={() => {
                            logout();
                            navigate("/login");
                        }}
                        className="text-white/60 hover:text-brand-danger transition p-2"
                        title="Esci"
                    >
                        <LogOut size={16} />
                    </button>
                </div>
            </div>
        </aside>
    );
}

export function BottomNav() {
    return (
        <nav
            data-testid="bottom-nav"
            className="lg:hidden fixed bottom-0 inset-x-0 bg-brand-navy/95 backdrop-blur-md border-t border-white/10 z-30"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
            <div
                className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar"
                data-testid="bottom-nav-scroller"
            >
                {NAV.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink
                        key={to}
                        to={to}
                        data-testid={`${testid}-mobile`}
                        className={({ isActive }) =>
                            `flex flex-col items-center justify-center gap-0.5 py-2.5 px-3 min-w-[68px] snap-start shrink-0 text-[9px] font-semibold uppercase tracking-wider transition ${
                                isActive ? "text-brand-gold" : "text-white/60"
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <Icon size={17} strokeWidth={isActive ? 2.4 : 1.8} />
                                <span className="text-[8px] whitespace-nowrap">{label}</span>
                            </>
                        )}
                    </NavLink>
                ))}
            </div>
        </nav>
    );
}

function AiCreditsWidget() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const credits = user?.unlimited_ai ? "∞" : (user?.ai_credits ?? 0);
    const low = !user?.unlimited_ai && (user?.ai_credits ?? 0) < 10;
    return (
        <button
            onClick={() => navigate("/profile?tab=credits")}
            data-testid="sidebar-ai-credits"
            className={`w-full flex items-center gap-2.5 p-3 rounded-lg transition ${
                low
                    ? "bg-amber-500/15 border border-amber-400/40 hover:bg-amber-500/25"
                    : "bg-white/5 hover:bg-white/10"
            }`}
        >
            <Sparkles size={16} className={low ? "text-amber-300" : "text-brand-gold"} />
            <div className="flex-1 text-left">
                <div className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                    Crediti AI
                </div>
                <div className={`text-sm font-bold ${low ? "text-amber-200" : "text-white"}`}>
                    {credits} {low ? "· in esaurimento" : ""}
                </div>
            </div>
            {!user?.unlimited_ai && (
                <span className="text-[9px] uppercase tracking-wider font-bold text-brand-gold/80">
                    +
                </span>
            )}
        </button>
    );
}
